import React from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

interface MonthPickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  // Keeping the old interface for backward compatibility
  selectedYear?: number;
  selectedMonth?: number; // 1-based month
}

/**
 * Component for selecting a month and year to view
 */
const MonthPicker: React.FC<MonthPickerProps> = ({
  selectedDate,
  onDateChange,
  selectedYear,
  selectedMonth, // 1-based
}) => {
  // Use provided date or create one from year/month for backward compatibility
  let dayjsDate: Dayjs;
  
  if (selectedDate) {
    dayjsDate = dayjs(selectedDate);
  } else if (selectedYear !== undefined && selectedMonth !== undefined) {
    // Create a dayjs object for the selected date
    dayjsDate = dayjs().year(selectedYear).month(selectedMonth - 1); // Dayjs months are 0-based
  } else {
    // Default to current date
    dayjsDate = dayjs();
  }
  
  // Handle date change from the DatePicker
  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      // Create a JavaScript Date object from the Dayjs object
      const jsDate = newDate.toDate();
      
      // For logging purposes
      const year = newDate.year();
      const month = newDate.month() + 1; // Convert 0-based month to 1-based
      console.log(`MonthPicker: Selected ${year}-${month}`);
      
      // Call the provided callback with the new Date object
      onDateChange(jsDate);
    }
  };

  return (
    <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
      <DatePicker
        label="Select Month"
        value={dayjsDate}
        onChange={handleDateChange}
        views={['month', 'year']}
        openTo="month"
        format="MMMM YYYY"
        slotProps={{
          textField: {
            fullWidth: true,
            variant: "outlined",
            sx: {
              '& .MuiInputBase-input': {
                padding: '12px',
                fontSize: '1rem',
              }
            }
          },
        }}
      />
    </Box>
  );
};

export default MonthPicker;
